import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { conditionalValidator } from 'src/app/shared/validators/conditional-validator';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { AzureAdTenant } from '../models/ad-tenant';
import { AzureADSettings } from '../models/azure-ad-settings';
import { AzureAdService } from '../services/azure-ad.service';
import { GridService } from 'src/app/shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule } from 'devextreme-angular/ui/nested';

@Component({
    templateUrl: './azure-ad-settings.component.html',
    styleUrls: ['./azure-ad-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, ReactiveFormsModule, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule]
})
export class AzureAdSettingsComponent implements OnInit, OnDestroy {
  public allowUpdating = false;
  public allowAdding = false;
  public allowDeleting = false;

  public tenants: AzureAdTenant[] = [];

  public editMode = false;
  public form: UntypedFormGroup = null;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  azureAdSettings = new AzureADSettings();
  enableAzureADChangesSubscription: Subscription = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private azureAdService: AzureAdService,
    private loadingPanelService: LoadingPanelService,
    private toastService: ToastService,
    private currentUserService: CurrentUserService,
    private gridService: GridService) {
  }

  async ngOnInit(): Promise<void> {
    this.allowAdding = this.currentUserService.hasPermission('AzureAD_Action_Create');
    this.allowUpdating = this.currentUserService.hasPermission('AzureAD_Action_Edit');
    this.allowDeleting = this.currentUserService.hasPermission('AzureAD_Action_Delete');

    this.form = this.formBuilder.group({
      enableLinkUser: [false],
      enableAzureAD: [false],
      clientId: ['', Validators.required],
      clientSecret: ['', conditionalValidator(control => !this.hasSecret, Validators.required)],
    });

    // Ensure that it is not possible to disable the kind of authentication
    // that the current user is using; thereby prevent the user from locking
    // him-/herself out of the system.
    if (this.currentUserService.isAzureADUser) {
      this.form.patchValue({ enableAzureAD: true });
      this.form.controls.enableAzureAD.disable();
    }
    else {
      this.form.patchValue({ enableLinkUser: true });
      this.form.controls.enableLinkUser.disable();
    }

    // Disable client id and secret when Azure AD is not enabled
    this.enableAzureADChangesSubscription = this.fcEnableAzureAD.valueChanges.subscribe(value => {
      if (value) {
        this.fcClientId.enable();
        this.fcClientSecret.enable();
      }
      else {
        this.fcClientId.disable();
        this.fcClientSecret.disable();
      }
    });

    await this.refresh();
  }

  ngOnDestroy(): void {
    this.enableAzureADChangesSubscription?.unsubscribe();
    this.enableAzureADChangesSubscription = null;
  }

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');

      [
        this.tenants,
        this.azureAdSettings
      ] = await Promise.all([
        this.azureAdService.getTenants(),
        this.azureAdService.getAzureADSettings()
      ]);

      this.updateAzureSettingsUI();
    }
    catch (err) {
      console.error(err);
      this.toastService.showToast('Failed to load data...', ToastType.error);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  editClicked(): void {
    this.editMode = true;
  }

  cancelClicked(): void {
    this.form.reset();
    this.editMode = false;
    this.updateAzureSettingsUI();
  }

  async saveClicked(): Promise<void> {
    try {
      this.azureAdSettings.LinkUserEnabled = this.fcEnableLinkUsers.value ? true : false;
      this.azureAdSettings.AzureADUserEnabled = this.fcEnableAzureAD.value ? true : false;
      this.azureAdSettings.ClientId = this.fcClientId.value;

      if (this.fcClientSecret.dirty) {
        this.azureAdSettings.ClientSecretValue = this.fcClientSecret.value;
      }

      this.azureAdSettings = await this.azureAdService.saveAzureADSettings(this.azureAdSettings);

      this.editMode = false;
      this.updateAzureSettingsUI();
      this.toastService.showToast('Settings saved...', ToastType.success);
    }
    catch (err) {
      this.toastService.showToast('Failed to save settings ...', ToastType.error);
    }
  }

  updateAzureSettingsUI(): void {
    this.form.patchValue({
      enableLinkUser: this.azureAdSettings.LinkUserEnabled,
      enableAzureAD: this.azureAdSettings.AzureADUserEnabled,
      clientId: this.azureAdSettings.ClientId,
      clientSecret: '',
    });
  }

  get hasSecret(): boolean {
    return !!this.azureAdSettings.ClientSecretId || !!this.azureAdSettings.ClientSecretValue;
  }

  get fcEnableAzureAD(): UntypedFormControl {
    return this.form.get('enableAzureAD') as UntypedFormControl;
  }

  get fcClientId(): UntypedFormControl {
    return this.form.get('clientId') as UntypedFormControl;
  }

  get fcClientSecret(): UntypedFormControl {
    return this.form.get('clientSecret') as UntypedFormControl;
  }

  get fcEnableLinkUsers(): UntypedFormControl {
    return this.form.get('enableLinkUser') as UntypedFormControl;
  }

  onRowInserting = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data as AzureAdTenant, item => this.saveAzureAdTenant(item));
  };

  onRowUpdating = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData } as AzureAdTenant, item => this.saveAzureAdTenant(item), e.newData);
  };

  onRowRemoving = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data as AzureAdTenant, item => this.deleteAzureAdTenant(item));
  };

  onInitNewRow(ev: any) {
    ev.data = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Id: null, TenantId: '', Name: '', Approved: false
    } as AzureAdTenant;
  }

  async saveAzureAdTenant(tenant: AzureAdTenant): Promise<AzureAdTenant> {
    const newItem = await this.azureAdService.saveTenant(tenant);
    this.toastService.showToast('Tenant saved...', ToastType.success);
    return newItem;
  }

  async deleteAzureAdTenant(tenant: AzureAdTenant) {
    await this.azureAdService.deleteTenant(tenant.Id);
    this.toastService.showToast('Tenant deleted...', ToastType.success);
    return tenant;
  }
}
